/**
 * studentAssessmentRules — the words and numbers the student's assessment
 * pages show, pure: card states, a paper's sections as the student is told
 * about them before starting, and how a coding answer reads in the result.
 *
 * The server decides every one of these facts (studentAssessmentController);
 * this only says them.
 */

export const MODE_LABELS = { practice: "Practice", exam: "Exam", sectioned_exam: "Sectioned exam" };

/**
 * A card's state → its badge (a word `StatusBadge` resolves to a tone — "new"
 * is the brand tone) and its label.
 */
export const STATUS = {
  available: { tone: "success", label: "Available" },
  in_progress: { tone: "warning", label: "In progress" },
  completed: { tone: "info", label: "Completed" },
  expired: { tone: "danger", label: "Expired" },
  upcoming: { tone: "new", label: "Upcoming" },
};

export const STATUS_OPTIONS = Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label }));

export const ACTION_LABELS = {
  in_progress: "Continue",
  completed: "View results",
  available: "Start",
  upcoming: "View details",
};

/** Scheduled windows are to the minute — a date alone makes an evening start look open. */
export const formatSchedule = (value) =>
  new Date(value).toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

export const formatDuration = (seconds) => {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * A sectioned paper's sections, as the student is told about them before
 * starting: name, how many questions and coding problems, and — only when the
 * sections run in order — each one's own time.
 */
export function sectionsOf(assessment) {
  if (!assessment?.isSectioned) return [];
  const sequential = assessment.sectionNavigation === "sequential";
  return [...(assessment.sections || [])]
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((s, i) => ({
      key: s._id || String(i),
      title: s.title || `Section ${String.fromCodePoint(65 + i)}`,
      questions: (s.dynamicConfig?.rules || []).length
        ? Number(s.dynamicConfig.totalQuestions) || s.dynamicConfig.rules.reduce((n, r) => n + (Number(r.count) || 0), 0)
        : (s.questions || []).length,
      coding: (s.codingQuestions || []).length,
      minutes: sequential ? Number(s.timeLimit) || 0 : null,
    }));
}

/** What one section holds, in a few words. */
export const sectionContents = (s) => [
  s.questions ? plural(s.questions, "question") : "",
  s.coding ? plural(s.coding, "coding problem") : "",
].filter(Boolean).join(" + ") || "No questions";

export const VERDICT_LABELS = {
  passed: "Passed",
  wrong: "Wrong output",
  timeout: "Time limit exceeded",
  compile: "Did not compile",
  error: "Runtime error",
};
export const verdictLabel = (v) => VERDICT_LABELS[v] || "Not run";

export const VERDICT_TONES = { passed: "success", wrong: "danger", timeout: "warning", compile: "danger", error: "danger" };

/** A coding problem's state on the result page. */
export const CODING_STATE_LABELS = {
  pending: "Being checked",
  error: "Could not be checked",
  none: "Not answered",
};

/**
 * The chip on a reviewed question: Correct / Incorrect / Skipped — and for a
 * coding problem the tests it passed, or that it is still being checked.
 */
export function reviewResult(question, answer) {
  if (question?.questionType === "coding") {
    const c = question.coding || {};
    if (!answer?.isAnswered || c.state === "none") return { tone: "neutral", label: "Skipped" };
    if (c.state === "pending") return { tone: "warning", label: "Being checked" };
    if (c.state === "error") return { tone: "danger", label: "Could not be checked" };
    if (c.total > 0 && c.passed === c.total) return { tone: "success", label: "Correct" };
    return { tone: c.passed > 0 ? "warning" : "danger", label: `${c.passed || 0}/${c.total || 0} tests` };
  }
  if (answer?.isCorrect) return { tone: "success", label: "Correct" };
  if (answer?.isAnswered) return { tone: "danger", label: "Incorrect" };
  return { tone: "neutral", label: "Skipped" };
}

/** How long the result page keeps asking while coding answers are judged. */
export const PENDING_POLL_MS = 8000;
export const PENDING_POLL_LIMIT_MS = 5 * 60 * 1000;

/**
 * The exam's guards as a student meets them, in sentences — what a staff
 * preview lists because it does not enforce them.
 */
export function guardsOf(antiCheat = {}) {
  const ac = antiCheat || {};
  const out = [];
  if (ac.fullscreenRequired) out.push("The exam runs in full screen; leaving it is recorded.");
  if (ac.tabSwitchDetection) {
    out.push(ac.autoSubmitOnViolation
      ? `Leaving the exam tab is recorded; after ${plural(Number(ac.maxTabSwitches ?? 3), "time")} the exam is submitted automatically.`
      : "Leaving the exam tab is recorded.");
  }
  if (ac.disableCopyPaste) out.push("Copy and paste are blocked.");
  if (ac.disableRightClick) out.push("Right-click is blocked.");
  return out;
}
